import { Component, Input, OnDestroy, OnInit } from "@angular/core";

import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { ChartBindingObject, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { IntentService } from "@tylertech/intents-module";
import { IChartCriteria, IIntent } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";


export interface QuickLink
{
    Name: string;
    Link: IIntent;
}

// TODO - this ain't no chart....

@Component({
    selector: "utilities-quick-links-chart",
    templateUrl: "./quickLinksChart.tmpl.html",
    styleUrls: [
        "./quickLinksChart.component.scss"
    ],
    standalone: false
})
export class QuickLinksChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    @Input("chartId")
    private readonly _chartId: string;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;

    isLoaded: boolean;

    private items: Array<QuickLink> = [];
    rows: Array<Array<QuickLink>>;

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $loggingService: LoggingService,
        private readonly $intentService: IntentService
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get showChart(): boolean
    {
        return !!this.chartCriteria?.ErrorMessage || this.chartHasData;
    }

    get chartHasData(): boolean
    {
        return this.rows?.length > 0;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn) =>
        {
            if (!this.chartHasData)
            {
                return this.loadChartData();
            }

            this.updateChartConfiguration();
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if ((this.chartCriteria?.ChartDatas?.length || 0) > 0)
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse | unknown) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: typeof error === "object"
                            ? error["error"]?.Message
                            : JSON.stringify(error),
                    } as IChartCriteria;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private updateChartConfiguration(): void
    {
        this.chartCriteria.ChartDatas[0].Data
            .forEach(chartData =>
            {
                const quickLink: QuickLink = {
                    Name: chartData.Name,
                    Link: {
                        MIME: chartData.MIME,
                        Action: chartData.Action,
                    },
                };
                this.items.push(quickLink);
                this.rows = this.chunk(this.items, 2);
            });
    }

    private chunk<T>(array: T[], size: number): T[][]
    {
        return array.reduce(
            (rows: Array<Array<T>>, item: T) =>
            {
                let last = rows[rows.length - 1];
                if (last.length >= size)
                {
                    last = [item];
                    rows.push(last);
                }
                else
                {
                    last.push(item);
                }

                return rows;
            },
            [[]]);
    }

    onLinkClicked(intent: IIntent): void
    {
        this.$intentService.resolveIntent(intent);
    }

    getAriaLabel(link: QuickLink): string
    {
        return `Open-${link.Name}-QuickLinks-UtilityBilling`;
    }
}
